import { useForm, useStore } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { SHIPPING_LABEL_COUNTRIES } from '@ivario-arc/shared/src/constants';
import { CountryEnum } from '@ivario-arc/shared/src/enums';
import { useNavigate } from 'react-router';
import { useMemo } from 'react';

import { ButtonType, SampleDto } from '@src/types/types';
import { Button } from '@components/atoms/Button';
import Input from '@components/atoms/Input';
import { Dropdown } from '@components/atoms/Dropdown';
import { apiClient } from '@src/api/client';
import { Paths } from '@src/routes/paths';

const countryOptions = [
  CountryEnum.Germany,
  CountryEnum.Austria,
  CountryEnum['United Kingdom'],
  CountryEnum.Switzerland,
  CountryEnum.Belgium,
  CountryEnum.Bulgaria,
  CountryEnum.Denmark,
  CountryEnum.Estonia,
  CountryEnum.Finland,
  CountryEnum.France,
  CountryEnum.Greece,
  CountryEnum.Ireland,
  CountryEnum.Italy,
  CountryEnum.Croatia,
  CountryEnum.Latvia,
  CountryEnum.Lithuania,
  CountryEnum.Luxembourg,
  CountryEnum.Malta,
  CountryEnum.Netherlands,
  CountryEnum.Poland,
  CountryEnum.Portugal,
  CountryEnum.Romania,
  CountryEnum.Sweden,
  CountryEnum.Slovakia,
  CountryEnum.Slovenia,
  CountryEnum.Spain,
  CountryEnum['Czech Republic'],
  CountryEnum.Hungary,
  CountryEnum.Cyprus,
];

export interface SenderAddressFormData {
  shippingPostalCode?: string;
  shippingStreet?: string;
  shippingStreetNumber?: string;
  shippingSenderFirstName?: string;
  shippingSenderLastName?: string;
  shippingCity?: string;
  shippingCountry?: CountryEnum;
  shippingSenderCompany?: string;
}

export const SenderAddressForm = ({ sample }: { sample: SampleDto }) => {
  const { t } = useTranslation('view', { keyPrefix: 'senderAddressForm' });
  const { t: tCountry } = useTranslation('countries');
  const { t: tError } = useTranslation('errors');
  const navigate = useNavigate();

  const { mutateAsync, error } = apiClient.useMutation(
    'post',
    '/api/sample/updateAddress',
    {
      onSuccess: () => {
        navigate(`${Paths.SAMPLE_COMMENT}/${sample.id}`);
      },
    },
  );

  const defaultValues: SenderAddressFormData = useMemo(() => {
    return {
      shippingSenderLastName: sample.shippingSenderLastName ?? '',
      shippingSenderFirstName: sample.shippingSenderFirstName ?? '',
      shippingStreet: sample.shippingStreet ?? '',
      shippingStreetNumber: sample.shippingStreetNumber ?? '',
      shippingPostalCode: sample.shippingPostalCode ?? '',
      shippingCity: sample.shippingCity ?? '',
      shippingCountry:
        (sample.shippingCountry as CountryEnum | undefined) ??
        CountryEnum.Germany,
      shippingSenderCompany: sample.shippingSenderCompany ?? '',
    };
  }, [
    sample.shippingCity,
    sample.shippingCountry,
    sample.shippingPostalCode,
    sample.shippingSenderCompany,
    sample.shippingSenderFirstName,
    sample.shippingSenderLastName,
    sample.shippingStreet,
    sample.shippingStreetNumber,
  ]);

  const form = useForm({
    defaultValues: defaultValues,
    onSubmit: async ({ value }) => {
      await mutateAsync({ body: { ...value, id: sample.id } });
    },
  });

  const validateNonEmpty = (value: string | undefined) =>
    !value?.length ? tError('emptyValue') : undefined;

  const country = useStore(form.store, (state) => state.values.shippingCountry);
  const addressFieldsMandatory = useMemo(() => {
    if (!country) {
      return false;
    }
    return SHIPPING_LABEL_COUNTRIES.includes(country);
  }, [country]);

  return (
    <div className="max-w-2xl">
      <h1>{t('title')}</h1>
      <h2>{t('subtitle')}</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void form.handleSubmit();
        }}
        className="space-y-6"
      >
        <form.Field
          name="shippingCountry"
          validators={{
            onBlur: ({ value }) => validateNonEmpty(value),
          }}
          children={(field) => (
            <Dropdown
              label={t('country')}
              value={
                countryOptions.find((c) => c === field.state.value) ||
                countryOptions[0]
              }
              options={countryOptions}
              displayValue={(option) => tCountry(option)}
              handleChange={(value) => field.handleChange(value)}
              errors={field.state.meta.errors}
              touched={field.state.meta.isTouched}
              testId={`address-form-input-${field.name}`}
            />
          )}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <form.Field
            name="shippingSenderLastName"
            validators={{
              onBlur: ({ value }) => validateNonEmpty(value),
            }}
            children={(field) => (
              <Input
                label={t('lastName')}
                value={field.state.value ?? ''}
                handleChange={(e) => field.handleChange(e.target.value)}
                handleBlur={field.handleBlur}
                type="text"
                name={field.name}
                id={field.name}
                errors={field.state.meta.errors}
                touched={field.state.meta.isTouched}
                testId={`address-form-input-${field.name}`}
              />
            )}
          />
          <form.Field
            name="shippingSenderFirstName"
            validators={{
              onBlur: ({ value }) => validateNonEmpty(value),
            }}
            children={(field) => (
              <Input
                label={t('firstName')}
                value={field.state.value ?? ''}
                handleChange={(e) => field.handleChange(e.target.value)}
                handleBlur={field.handleBlur}
                type="text"
                name={field.name}
                id={field.name}
                errors={field.state.meta.errors}
                touched={field.state.meta.isTouched}
                testId={`address-form-input-${field.name}`}
              />
            )}
          />
        </div>
        {addressFieldsMandatory && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <form.Field
                name="shippingStreet"
                validators={{
                  onBlur: ({ value }) =>
                    !addressFieldsMandatory || validateNonEmpty(value),
                }}
                children={(field) => (
                  <Input
                    label={t('street')}
                    value={field.state.value ?? ''}
                    handleChange={(e) => field.handleChange(e.target.value)}
                    handleBlur={field.handleBlur}
                    type="text"
                    name={field.name}
                    id={field.name}
                    errors={field.state.meta.errors}
                    touched={field.state.meta.isTouched}
                    testId={`address-form-input-${field.name}`}
                  />
                )}
              />
              <form.Field
                name="shippingStreetNumber"
                validators={{
                  onBlur: ({ value }) =>
                    !addressFieldsMandatory || validateNonEmpty(value),
                }}
                children={(field) => (
                  <Input
                    label={t('houseNumber')}
                    value={field.state.value ?? ''}
                    handleChange={(e) => field.handleChange(e.target.value)}
                    handleBlur={field.handleBlur}
                    type="text"
                    name={field.name}
                    id={field.name}
                    errors={field.state.meta.errors}
                    touched={field.state.meta.isTouched}
                    testId={`address-form-input-${field.name}`}
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <form.Field
                name="shippingPostalCode"
                validators={{
                  onBlur: ({ value }) =>
                    !addressFieldsMandatory || validateNonEmpty(value),
                }}
                children={(field) => (
                  <Input
                    label={t('postalCode')}
                    value={field.state.value ?? ''}
                    handleChange={(e) => field.handleChange(e.target.value)}
                    handleBlur={field.handleBlur}
                    type="text"
                    name={field.name}
                    id={field.name}
                    errors={field.state.meta.errors}
                    touched={field.state.meta.isTouched}
                    testId={`address-form-input-${field.name}`}
                  />
                )}
              />
              <form.Field
                name="shippingCity"
                validators={{
                  onBlur: ({ value }) =>
                    !addressFieldsMandatory || validateNonEmpty(value),
                }}
                children={(field) => (
                  <Input
                    label={t('city')}
                    value={field.state.value ?? ''}
                    handleChange={(e) => field.handleChange(e.target.value)}
                    handleBlur={field.handleBlur}
                    type="text"
                    name={field.name}
                    id={field.name}
                    errors={field.state.meta.errors}
                    touched={field.state.meta.isTouched}
                    testId={`address-form-input-${field.name}`}
                  />
                )}
              />
            </div>
          </>
        )}

        <form.Field
          name="shippingSenderCompany"
          children={(field) => (
            <Input
              label={t('companyName')}
              value={field.state.value ?? ''}
              handleChange={(e) => field.handleChange(e.target.value)}
              handleBlur={field.handleBlur}
              type="text"
              name={field.name}
              id={field.name}
              errors={field.state.meta.errors}
              touched={field.state.meta.isTouched}
              testId={`address-form-input-${field.name}`}
            />
          )}
        />

        {error && (
          <div className="text-error" data-testid="address-form-error">
            {tError('saveAddress')}
          </div>
        )}
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => (
            <Button
              buttonType={ButtonType.PRIMARY}
              label={t('saveSenderAddress')}
              type="submit"
              className="w-full mt-4"
              disabled={!canSubmit && !isSubmitting}
              loading={isSubmitting}
              testingId={`address-form-submit`}
            />
          )}
        />
      </form>
    </div>
  );
};
